import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";

const STAGE_COLORS: Record<string, string> = {
  new: "bg-gray-100 text-gray-700",
  screened: "bg-blue-100 text-blue-800",
  shortlisted: "bg-emerald-100 text-emerald-800",
  interviewed: "bg-violet-100 text-violet-800",
  offered: "bg-amber-100 text-amber-800",
  hired: "bg-emerald-200 text-emerald-900",
  rejected: "bg-rose-100 text-error-text",
  archived: "bg-surface-secondary text-text-secondary",
};

export function StageBadge({ stage }: { stage: string | null }) {
  if (!stage) return null;
  const color = STAGE_COLORS[stage] ?? "bg-surface-secondary text-text-secondary";
  return (
    <span className={`inline-flex rounded-badge px-2 py-0.5 text-[11px] font-semibold capitalize ${color}`}>
      {stage}
    </span>
  );
}

export function StageActions({ candidateId, currentStage }: { candidateId: number; currentStage: string | null }) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const stage = currentStage || "new";

  const mutation = useMutation({
    mutationFn: (body: { candidate_id: number; action: string; stage?: string; notes?: string }) =>
      api.runLifecycleAgent(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.candidateStages(candidateId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.events({ entityType: "candidate", entityId: candidateId }) });
      setNotes("");
    },
  });

  const isTerminal = stage === "hired" || stage === "rejected" || stage === "archived";

  return (
    <div className="space-y-2">
      <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
        Pipeline actions
      </div>
      <div className="flex items-center gap-2">
        <StageBadge stage={stage} />
        {!isTerminal && (
          <button
            onClick={() => mutation.mutate({ candidate_id: candidateId, action: "advance", notes: notes || undefined })}
            disabled={mutation.isPending}
            className="rounded-btn bg-near-black px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {mutation.isPending ? "..." : "Advance"}
          </button>
        )}
        {stage !== "rejected" && stage !== "archived" && (
          <button
            onClick={() => mutation.mutate({ candidate_id: candidateId, action: "reject", notes: notes || undefined })}
            disabled={mutation.isPending}
            className="rounded-btn px-3 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text disabled:opacity-50"
          >
            Reject
          </button>
        )}
        {stage === "rejected" && (
          <button
            onClick={() => mutation.mutate({ candidate_id: candidateId, action: "move_to", stage: "screened", notes: "Reconsidered" })}
            disabled={mutation.isPending}
            className="rounded-btn px-3 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-emerald-100 hover:text-emerald-800 disabled:opacity-50"
          >
            Reconsider
          </button>
        )}
      </div>
      {/* Quick notes input */}
      <input
        type="text"
        placeholder="Add notes (optional)..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className="w-full rounded-btn border border-border-card bg-white px-2.5 py-1 text-xs text-near-black focus:outline-none focus:shadow-focus"
      />
      {mutation.isError && (
        <p className="text-xs text-error-text">{(mutation.error as Error).message}</p>
      )}
    </div>
  );
}
